import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { useSelector } from "react-redux";

const API = process.env.REACT_APP_API_URL;

export default function ProductMaster() {
  const { userInfo } = useSelector((state) => state.userLogin || {});
  const { getRoleData } = useSelector((state) => state.getRoleInfo || {});
  const role = getRoleData || userInfo?.role || {};
  const canAdd = !!role.productMasterAdd;
  const canUpdate = !!role.productMasterUpdate;
  const canDelete = !!role.productMasterDelete;
  const columnCount = canUpdate || canDelete ? 4 : 3;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");

  const authConfig = {
    headers: userInfo?.token ? { Authorization: `Bearer ${userInfo.token}` } : {},
  };

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      setMessage("");
      const { data } = await axios.get(`${API}/products`, authConfig);
      setProducts(data?.items || []);
    } catch (error) {
      setMessage(error?.response?.data?.message || "Unable to load products.");
    } finally {
      setLoading(false);
    }
  }, [userInfo?.token]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const openAdd = () => {
    setEditing(null);
    setName("");
    setMessage("");
    setFormOpen(true);
  };

  const openEdit = (product) => {
    setEditing(product);
    setName(product.name || "");
    setMessage("");
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setEditing(null);
    setName("");
  };

  const saveProduct = async (event) => {
    event.preventDefault();
    if (!name.trim()) {
      setMessage("Product name is required.");
      return;
    }
    try {
      setSaving(true);
      setMessage("");
      if (editing) {
        await axios.put(`${API}/products/${editing._id}`, { name: name.trim() }, authConfig);
      } else {
        await axios.post(`${API}/products`, { name: name.trim() }, authConfig);
      }
      setFormOpen(false);
      setEditing(null);
      setName("");
      await loadProducts();
    } catch (error) {
      setMessage(error?.response?.data?.message || "Unable to save product.");
    } finally {
      setSaving(false);
    }
  };

  const removeProduct = async (product) => {
    if (!window.confirm(`Delete “${product.name}”?`)) return;
    try {
      setMessage("");
      await axios.delete(`${API}/products/${product._id}`, authConfig);
      setProducts((current) => current.filter((item) => item._id !== product._id));
    } catch (error) {
      setMessage(error?.response?.data?.message || "Unable to delete product.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">Product Master</h1>
            <p className="mt-1 text-sm text-gray-500">Manage loan products.</p>
          </div>
          {canAdd && <button type="button" onClick={openAdd} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
            <Plus size={18} /> Add Product
          </button>}
        </div>

        {message && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{message}</div>}

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['S.No', 'Name', 'Created At', ...(canUpdate || canDelete ? ['Actions'] : [])].map((heading) => (
                    <th key={heading} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan={columnCount} className="px-5 py-12 text-center text-sm text-gray-500">Loading products...</td></tr>
                ) : products.length === 0 ? (
                  <tr><td colSpan={columnCount} className="px-5 py-12 text-center text-sm text-gray-500">No products added yet.</td></tr>
                ) : products.map((product, index) => (
                  <tr key={product._id} className="hover:bg-gray-50">
                    <td className="px-5 py-3 text-sm text-gray-600">{index + 1}</td>
                    <td className="px-5 py-3 text-sm font-medium text-gray-800">{product.name}</td>
                    <td className="px-5 py-3 text-sm text-gray-600">{dayjs(product.createdAt).format('DD MMM YYYY, hh:mm A')}</td>
                    {(canUpdate || canDelete) && <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        {canUpdate && <button type="button" onClick={() => openEdit(product)} className="rounded-md p-2 text-blue-600 hover:bg-blue-50" title="Edit"><Pencil size={16} /></button>}
                        {canDelete && <button type="button" onClick={() => removeProduct(product)} className="rounded-md p-2 text-red-600 hover:bg-red-50" title="Delete"><Trash2 size={16} /></button>}
                      </div>
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeForm}>
          <form onSubmit={saveProduct} onClick={(event) => event.stopPropagation()} className="w-full max-w-md rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-gray-800">{editing ? 'Edit Product' : 'Add Product'}</h2>
              <button type="button" onClick={closeForm} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-5">
              <label className="mb-1 block text-sm font-medium text-gray-700">Name</label>
              <input autoFocus value={name} onChange={(event) => setName(event.target.value)} placeholder="Enter product name" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-5 py-4">
              <button type="button" onClick={closeForm} disabled={saving} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-300">{saving ? 'Saving...' : editing ? 'Update' : 'Add'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
